import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransText } from '@/i18n/trans/text';
import { formatDateLLL } from '@/utils/dateTime';
import { Button, Icons, Typography } from '@fleet/webshop-ui';
import type { FC, ReactNode } from 'react';
import { useCallback } from 'react';

type MobileSearchWidgetProps = {
  children: ReactNode;
};

export const MobileSearchWidget: FC<MobileSearchWidgetProps> = ({
  children,
}) => {
  const {
    isSearchOverlayOpened,
    setIsSearchOverlayOpened,
    currentSearchFormValues,
  } = useSearchWidget();

  const getDates = useCallback(() => {
    const { departureDate, returnDate } = currentSearchFormValues || {};
    const dates: Array<JSX.Element> = [];

    if (departureDate) {
      const formattedDepartureDate = formatDateLLL(departureDate);
      dates.push(<>{formattedDepartureDate}</>);
    }

    if (returnDate) {
      const formattedReturnDate = formatDateLLL(returnDate);
      dates.push(<>{formattedReturnDate}</>);
    }

    return dates.map((date, index) => (
      <span key={index}>
        {index > 0 && ' - '}
        {date}
      </span>
    ));
  }, [currentSearchFormValues]);

  return (
    <>
      <Button
        variant="ghost"
        data-testid="mobile-search-trigger"
        className="mx-auto flex h-auto w-full items-stretch rounded-none p-4 text-dark"
        onPress={() => setIsSearchOverlayOpened(true)}
      >
        <Icons.search className="flex-shrink-0" />
        <div className="ml-3 flex w-full flex-col items-start overflow-hidden">
          {currentSearchFormValues?.origin &&
          currentSearchFormValues?.destination ? (
            <>
              <div className="flex items-center gap-2 overflow-hidden text-start">
                <Typography variant="subtitle" className="truncate">
                  {currentSearchFormValues.origin.cityName}
                </Typography>
                {currentSearchFormValues.returnDate ? (
                  <Icons.return className="flex-shrink-0" />
                ) : (
                  <Icons.oneWayAlt className="flex-shrink-0" />
                )}
                <Typography variant="subtitle" className="truncate">
                  {currentSearchFormValues.destination.cityName}
                </Typography>
              </div>
              <div className="flex w-full flex-row items-center gap-2 overflow-hidden">
                <Typography variant="body1" className="truncate">
                  {getDates()}
                </Typography>
                <Icons.dot className="flex-shrink-0" />
                <Typography variant="body1" className="truncate">
                  <TransText
                    i18nKey="passengersCount"
                    values={{
                      count: currentSearchFormValues.passengers.length,
                    }}
                  />
                </Typography>
              </div>
            </>
          ) : (
            <Typography variant="body1" className="truncate">
              <TransText i18nKey="pressHereToSearch" />
            </Typography>
          )}
        </div>
      </Button>

      {isSearchOverlayOpened && (
        <div
          className="fixed inset-0 z-50"
          data-testid="mobile-search-widget-sheet"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 duration-300 animate-in fade-in"
            onClick={() => setIsSearchOverlayOpened(false)}
          />
          {/* Panel */}
          <div className="absolute inset-x-0 bottom-0 flex max-h-[95vh] flex-col rounded-t-2xl border-t bg-white text-dark shadow-lg duration-500 animate-in slide-in-from-bottom">
            {/* Header */}
            <div className="flex items-center gap-3 px-3 pt-3">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Icons.search />
                <Typography
                  variant="subtitle"
                  as="h2"
                  className="min-w-0 truncate"
                >
                  <TransText i18nKey="editSearch" />
                </Typography>
              </div>
              <Button
                variant="icon"
                data-testid="mobile-search-widget-sheet-close-button"
                className="ml-auto shrink-0 text-dark"
                onPress={() => setIsSearchOverlayOpened(false)}
              >
                <Icons.xSmall height={24} width={24} />
                <span className="sr-only">Close</span>
              </Button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-auto p-4">{children}</div>
          </div>
        </div>
      )}
    </>
  );
};
